import { debugLog } from "@/logging/log-level"
import type {
  BlockchainConfig,
  EthereumBlockchainConfig,
} from "@anaconda/core/blockchain/types"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { computed, withInit } from "@reatom/core"
import { molecule } from "bunshi"
import { http, type Chain, type Transport, defineChain } from "viem"
import { BffApiClientMolecule } from "../api-client/store"
import { ApplicationEnvironmentMolecule } from "../env/store"
import { type QueryAtomState, reatomQuery } from "../reatom/reatom-query"
import { getFeaturedBlockchain } from "./featured-blockchain/get-featured-blockchain"
import { getBlockchainsQueryParams } from "./get-blockchains-query-params"
import { withRequiredClientBlockchains } from "./required-client-blockchains"

// Server-side data for hydration
// Initialize with proper structure to prevent null access errors
let serverBlockchainsData: QueryAtomState<{
  networks: readonly BlockchainConfig[]
}> = {
  data: undefined,
  isLoading: true,
  isPending: true,
  error: undefined,
  status: "pending",
  isError: false,
  isSuccess: false,
}

export function initializeBlockchainsData(
  data: QueryAtomState<{ networks: readonly BlockchainConfig[] }>,
) {
  serverBlockchainsData = data
  debugLog(
    "🟢 [BlockchainsMolecule] Initialized with server data:",
    data.data?.networks?.length,
    "networks",
  )
}

export const BlockchainsMoleculeReatom = molecule((mol) => {
  const bffApiClient = mol(BffApiClientMolecule)
  const { hostEnvironment } = mol(ApplicationEnvironmentMolecule)

  // Query atom using reatomQuery helper
  // Extended with withInit to hydrate from server-side prefetched data
  const blockchainsQueryParams = getBlockchainsQueryParams(bffApiClient)
  const blockchains = reatomQuery<{ networks: readonly BlockchainConfig[] }>(
    blockchainsQueryParams,
    "blockchains",
  ).extend(withInit(() => serverBlockchainsData))

  // Computed lists - filter and transform blockchain data
  const blockchainsList = computed(() => {
    const blockchainsData = blockchains()
    const networks = blockchainsData.data?.networks ?? []
    const filtered = networks.filter(
      (config) => config !== null && config !== undefined,
    ) as BlockchainConfig[]
    const withRequiredBlockchains = withRequiredClientBlockchains(
      filtered,
      hostEnvironment,
    )
    console.log(
      "🔍 [REATOM/BlockchainsMolecule] Blockchains list:",
      withRequiredBlockchains.map((b) => b.blockchain).join(", "),
    )
    return withRequiredBlockchains
  }, "blockchainsList")

  const mainnetBlockchains = computed(() => {
    const networks = blockchainsList()
    return networks
      .filter((b) => !b?.isTestnet)
      .map((b) => b?.blockchain)
      .filter((b) => b !== undefined)
  }, "mainnetBlockchains")

  const evmBlockchainsList = computed(() => {
    const networks = blockchainsList()
    const result = networks.filter((b) => b.isEvm) as EthereumBlockchainConfig[]
    return result
  }, "evmBlockchainsList")

  const evmNetworkMap = computed(() => {
    const networks = blockchainsList()
    const evmBlockchains = networks
      .filter((b) => (b as EthereumBlockchainConfig).isEvm)
      .reduce(
        (acc, b) => {
          const blockchain = b as EthereumBlockchainConfig
          acc[blockchain.chainId] = blockchain
          return acc
        },
        {} as Record<number, EthereumBlockchainConfig>,
      )
    return evmBlockchains
  }, "evmNetworkMap")

  const blockchainsMap = computed(() => {
    const networks = blockchainsList()
    const map = networks.reduce<Record<Blockchain, BlockchainConfig>>(
      (acc, b) => {
        acc[b.blockchain] = b
        return acc
      },
      {} as Record<Blockchain, BlockchainConfig>,
    )
    return map
  }, "blockchainsMap")

  const supportedBlockchains = computed(() => {
    const networks = blockchainsList()
    const supported = networks.map((b) => b.blockchain)
    debugLog(
      "🔍 [REATOM/BlockchainsMolecule] Supported blockchains computed:",
      supported,
    )
    return supported
  }, "supportedBlockchains")

  const defaultEvmNetworkInfo = computed(() => {
    const map = blockchainsMap()
    return map[getFeaturedBlockchain()] as EthereumBlockchainConfig
  }, "defaultEvmNetworkInfo")

  const viemNetworks = computed(() => {
    const evmList = evmBlockchainsList()
    const blockchainList = evmList.map((blockchain) =>
      defineChain(blockchain.viem),
    )
    return blockchainList as [Chain, ...Chain[]]
  }, "viemNetworks")

  const transports = computed(() => {
    const evmList = evmBlockchainsList()
    const transportMap = evmList.reduce(
      (acc, blockchain) => {
        const rpcUrl = blockchain.rpcUrls[0].url
        if (rpcUrl) {
          acc[blockchain.chainId] = http(rpcUrl)
        }
        return acc
      },
      {} as Record<number, Transport>,
    )
    return transportMap
  }, "transports")

  // Factory functions (replace atomFamily)
  // These return functions that directly compute values from blockchainsList()
  // to avoid hydration issues with computed atoms
  const reatomFeesVersion = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const me = networks.find((b) => b.blockchain === blockchain)
    const result = me && "feesVersion" in me ? me.feesVersion : "v3"
    return result
  }

  const reatomTreasury = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const me = networks.find((b) => b.blockchain === blockchain)
    return me && "treasury" in me ? me.treasury : undefined
  }

  const reatomDefaultCurrencyId = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const me = networks.find((b) => b.blockchain === blockchain)
    return me?.primaryCurrency
  }

  const reatomWrappedCurrencyId = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const me = networks.find((b) => b.blockchain === blockchain)
    return me?.wrappedCurrency
  }

  const reatomAddFundsLink = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const me = networks.find((b) => b.blockchain === blockchain)
    return me?.addFundsLink
  }

  const reatomIsEvmBlockchain = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const me = networks.find((b) => b.blockchain === blockchain)
    return me ? (me as EthereumBlockchainConfig).isEvm : false
  }

  const reatomIsSolanaBlockchain = () => (blockchain: Blockchain) => {
    return blockchain === Blockchain.SOLANA
  }

  const reatomIsConnectedNetworkSupported = () => (chainId: number) => {
    const supportedInfo = blockchainsList()
    return supportedInfo.some(
      (b) =>
        (b as EthereumBlockchainConfig).isEvm &&
        (b as EthereumBlockchainConfig).chainId === chainId,
    )
  }

  const reatomScannerLinkByChainId = () => (chainId: number) => {
    const me = blockchainsList().find(
      (blockchain) => blockchain.chainId === chainId,
    )
    return me?.blockExplorers[0]?.url
  }

  const reatomScannerLinkByBlockchain = () => (blockchain: Blockchain) => {
    const me = blockchainsList().find((b) => b.blockchain === blockchain)
    return me?.blockExplorers[0]?.url
  }

  const reatomBlockchainChainName = () => (blockchain: Blockchain) => {
    const evmList = evmBlockchainsList()
    const me = evmList.find((b) => b.blockchain === blockchain)
    return me?.chainName
  }

  const reatomChainId = () => (blockchain: Blockchain) => {
    const networks = blockchainsList()
    const blockchainConfig = networks.find((b) => b.blockchain === blockchain)
    if (!blockchainConfig) throw new Error(`${blockchain} blockchain not found`)
    return blockchainConfig.chainId
  }

  return {
    // Main data atom
    blockchains,

    // Computed lists (with aliases)
    blockchainsList,
    reatomBlockchainsList: blockchainsList,
    mainnetBlockchains,
    reatomMainnetBlockchains: mainnetBlockchains,
    evmBlockchainsList,
    reatomEvmBlockchainsList: evmBlockchainsList,
    evmNetworkMap,
    reatomEvmNetworkMap: evmNetworkMap,
    blockchainsMap,
    reatomBlockchainsMap: blockchainsMap,
    supportedBlockchains,
    reatomSupportedBlockchains: supportedBlockchains,
    defaultEvmNetworkInfo,
    reatomDefaultEvmNetworkInfo: defaultEvmNetworkInfo,

    // Network configs
    viemNetworks,
    transports,

    // Factory functions (replace atomFamily)
    reatomFeesVersion,
    reatomTreasury,
    reatomDefaultCurrencyId,
    reatomWrappedCurrencyId,
    reatomAddFundsLink,
    reatomIsEvmBlockchain,
    isEvmBlockchain: reatomIsEvmBlockchain,
    reatomIsSolanaBlockchain,
    isSolanaBlockchain: reatomIsSolanaBlockchain,
    reatomIsConnectedNetworkSupported,
    reatomScannerLinkByChainId,
    reatomScannerLinkByBlockchain,
    reatomBlockchainChainName,
    reatomChainId,

    // Query params helper
    getBlockchainsQueryParams: () => getBlockchainsQueryParams(bffApiClient),
  }
})
